import { api, auth, unwrap } from "@lumberset/api-client";
import { Button, Field, Heading, Hint, Input, Logo, RadioGroup, Select } from "@lumberset/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { signupStatusQuery } from "../lib/queries";
import {
  type BuilderTrade,
  builderTrades,
  tradeLabels,
  type WorkspaceKind,
} from "../lib/workspace";

export const Route = createFileRoute("/onboarding")({
  validateSearch: (search: Record<string, unknown>): { kind?: "builder" } =>
    search.kind === "builder" ? { kind: "builder" } : {},
  beforeLoad: async () => {
    const { data } = await auth.getSession();
    if (!data) throw redirect({ to: "/login", search: { redirect: "/onboarding" } });
  },
  component: Onboarding,
});

const slugify = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);

function Onboarding() {
  const router = useRouter();
  const search = Route.useSearch();
  const [kind, setKind] = useState<WorkspaceKind>(search.kind === "builder" ? "builder" : "yard");
  const [trade, setTrade] = useState<BuilderTrade>("general_contractor");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const builder = kind === "builder";
  const { data: status } = useQuery(signupStatusQuery);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = String(new FormData(event.currentTarget).get("name")).trim();
    setBusy(true);
    setError(null);

    // A short random tail keeps two "Main Street Lumber" yards from colliding.
    const tail = Math.random().toString(36).slice(2, 6);
    const result = await auth.organization.create({
      name,
      slug: `${slugify(name) || kind}-${tail}`,
    });

    if (result.error) {
      setBusy(false);
      setError(result.error.message ?? `We could not set up your ${builder ? "company" : "yard"}.`);
      return;
    }

    // The workspace exists with a plain profile. Say what kind it is before the first screen loads.
    try {
      await unwrap(
        api.yard.profile.$patch({ json: builder ? { kind: "builder", trade } : { kind: "yard" } }),
      );
    } catch (failure) {
      setBusy(false);
      setError(failure instanceof Error ? failure.message : "We could not save your workspace.");
      return;
    }
    await router.invalidate();
    router.history.push("/");
  }

  async function signOut() {
    await auth.signOut();
    await router.invalidate();
    router.history.push("/login");
  }

  // Closed by an Admin: no new workspaces. Someone invited joins theirs from the invitation link.
  if (status?.open === false) {
    return (
      <div className="pt-safe pb-safe flex min-h-dvh flex-col items-center justify-center bg-ui-bg-subtle px-4 py-10">
        <Logo className="mb-8 text-[17px]" markClassName="size-7" />
        <div className="flex w-full max-w-[440px] flex-col gap-y-5 rounded-xl bg-ui-bg-base p-6 shadow-elevation-card-rest sm:p-8">
          <div>
            <Heading>Sign-up is closed for now</Heading>
            <p className="txt-small mt-1 text-pretty text-ui-fg-subtle">
              New workspaces cannot be set up at the moment. If a team invited you, open the link in
              your invitation email to join it.
            </p>
          </div>
          <Button size="large" variant="secondary" block onClick={signOut}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-safe pb-safe flex min-h-dvh flex-col items-center justify-center bg-ui-bg-subtle px-4 py-10">
      <Logo className="mb-8 text-[17px]" markClassName="size-7" />
      <form
        onSubmit={submit}
        className="flex w-full max-w-[440px] flex-col gap-y-5 rounded-xl bg-ui-bg-base p-6 shadow-elevation-card-rest sm:p-8"
      >
        <div>
          <Heading>{builder ? "Set up your company" : "Set up your yard"}</Heading>
          <p className="txt-small mt-1 text-ui-fg-subtle">
            {builder
              ? "This is the name yards will see on your requests for quotes. You can invite your team afterwards."
              : "This is the name your customers will see on quotes. You can invite your team afterwards."}
          </p>
        </div>

        <RadioGroup value={kind} onValueChange={(value) => setKind(value as WorkspaceKind)}>
          <RadioGroup.ChoiceBox
            value="yard"
            label="Lumber yard"
            description="I sell lumber and quote my customers."
          />
          <RadioGroup.ChoiceBox
            value="builder"
            label="Builder"
            description="I buy lumber and want takeoffs, budgets and quotes from yards."
          />
        </RadioGroup>

        <Field label={builder ? "Company name" : "Yard name"}>
          {(control) => (
            <Input
              {...control}
              name="name"
              placeholder={builder ? "Northside Framing" : "Main Street Lumber"}
              required
              size="large"
              autoFocus
            />
          )}
        </Field>

        {builder ? (
          <Field label="What you do">
            {(control) => (
              <Select value={trade} onValueChange={(value) => setTrade(value as BuilderTrade)}>
                <Select.Trigger id={control.id} size="large">
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  {builderTrades.map((value) => (
                    <Select.Item key={value} value={value}>
                      {tradeLabels[value]}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
            )}
          </Field>
        ) : null}

        {error ? <Hint variant="error">{error}</Hint> : null}

        <Button type="submit" size="large" block isLoading={busy}>
          Continue
        </Button>

        <ul className="txt-small flex flex-col gap-y-1.5 border-t border-ui-border-base pt-5 text-ui-fg-subtle">
          <li>Your first takeoff is free.</li>
          {builder ? (
            <>
              <li>Your drawings and the quotes you receive stay private to your company.</li>
              <li>Budget prices come from our reference price book. A yard's quote will differ.</li>
            </>
          ) : (
            <>
              <li>Your drawings, catalog and prices stay private to your yard.</li>
              <li>We start you on common framing standards. Change any of them in Settings.</li>
            </>
          )}
        </ul>
      </form>
    </div>
  );
}
